import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { SiqError, SleeperRegistrationState } from "@models/app/helpers.model";
import { CognitoLoginData, CognitoLoginModel, CognitoQuery, CognitoRefreshToken, CognitoRefreshTokenModel, UserInfo } from "@models/auth/cognito.model";
import { LoginModel, LoginResponse } from "@models/auth/login.model";
import { Navigate } from "@ngxs/router-plugin";
import { Action, State, StateContext } from "@ngxs/store";
import { AuthService } from "@services/auth.service";
import { MixpanelService } from "@services/mixpanel.service";
import { SplashScreenService } from "@services/splash-screen.service";
import { CognitoErrorCodes, RememberMe, SiqErrorCodes } from "@shared/utils/helpers/enum.helper";
import { FunctionsHelper } from "@shared/utils/helpers/functions.helper";
import { ErrorCodeHelper, ServerErrors } from "@shared/utils/helpers/siq-errors.helper";
import { SiqPopupHelper } from "@shared/utils/helpers/siq-popup.helper";
import { ResetAppState } from "@store/app/app.actions";
import { ResetBedState, UnsubscribeFromBedStatus } from "@store/beds/beds.actions";
import { ResetEdpState } from "@store/edp/edp.actions";
import { ResetForgotPasswordState } from "@store/forgot-password/forgot-password.actions";
import { ResetHealthState } from "@store/health/health.actions";
import { ResetPasswordRecoveryState } from "@store/password-recovery/password-recovery.actions";
import { ResetRegisterState } from "@store/register/register.actions";
import { ResetSessionsState } from "@store/sessions/sessions.actions";
import { ResetSettingsState } from "@store/settings/settings.actions";
import { ResetSetupState } from "@store/setup/setup.actions";
import { ResetSleeperState } from "@store/sleepers/sleepers.actions";
import { ResetTacoState, SetTacoState } from "@store/taco/taco.actions";
import { ResetValidateLoginState } from "@store/validate-login/validate-login.actions";
import { ResetWhyIsThisState } from "@store/why-is-this/why-is-this.actions";
import { Observable, catchError, map, of, tap, throwError } from "rxjs";
import { BootController } from 'src/boot-control';
import { CognitoLogin, GetUserInformation, GoToLogin, Logout, NativeLogin, ReLogin, ReSiqLogin, ResetAuthState, ResetStates, SetCognitoAuth, SetLoginData, SetRegistrationState, SetRememberMe, UnpackToken } from "./auth.actions";
import { AuthStateModel, defaultAuthState } from "./auth.model";

@State<AuthStateModel>({
  name: 'auth',
  defaults: defaultAuthState
})
@Injectable()
export class AuthState {

  constructor(
    private authService: AuthService,
    private ngZone: NgZone,
    private mixpanelService: MixpanelService,
    private splashScreenService: SplashScreenService,
    private siqPopupHelper: SiqPopupHelper) { }

  @Action(CognitoLogin)
  cognitoLogin(ctx: StateContext<AuthStateModel>, action: CognitoLogin): Observable<CognitoQuery<CognitoLoginData>> {
    ctx.dispatch(new SetLoginData(action.payload));
    ctx.patchState({ loading: true });
    this.splashScreenService.setAuthLoading(true);
    return this.authService.cognitoLogin(action.payload).pipe(
      tap({
        next: (result: CognitoQuery<CognitoLoginData>) => {
          // calculate token expiration time
          const expiresAt = this.calculateTokenExpirationTime(result.data.ExpiresIn ?? 0);

          ctx.patchState({
            loading: false,
            cognitoAuth: FunctionsHelper.encrypt({...result, ExpiresAt: expiresAt}),
            refreshToken: FunctionsHelper.encrypt(result.data.RefreshToken),
            error: null,
            siqAuth: null
          });
          ctx.dispatch(new UnpackToken());
        },
        error: error => this.handleCognitoLoginError(ctx, error)
      })
    );
  }

  @Action(SetLoginData)
  setLoginData(ctx: StateContext<AuthStateModel>, action: SetLoginData): void {
    ctx.patchState({ loading: false });
    localStorage.setItem('login', FunctionsHelper.encrypt(action.payload));
  }

  @Action(SetCognitoAuth)
  setCognitoAuth(ctx: StateContext<AuthStateModel>, action: SetCognitoAuth): void {
    ctx.patchState({ cognitoAuth: FunctionsHelper.encrypt(action.payload) });
  }

  @Action(ReLogin)
  reLogin(ctx: StateContext<AuthStateModel>): Observable<CognitoQuery<CognitoRefreshToken>> {
    const reLoginData = Object.assign(new CognitoRefreshTokenModel({ RefreshToken: FunctionsHelper.decrypt<string>(ctx.getState().refreshToken) }));
    return this.authService.cognitoRefreshToken(reLoginData).pipe(
      tap({
        next: (result: CognitoQuery<CognitoRefreshToken>) => {
          // update the token expiration time after refreshing the token
          const expiresAt = this.calculateTokenExpirationTime(result.data.ExpiresIn ?? 0);
          
          ctx.patchState({ cognitoAuth: FunctionsHelper.encrypt({...result, ExpiresAt: expiresAt}) });
          const username = FunctionsHelper.decodeJWT(result.data.AccessToken)['username'];
          ctx.patchState({ username: FunctionsHelper.encrypt(username) });
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }


  @Action(NativeLogin)
  nativeLogin(ctx: StateContext<AuthStateModel>, action: NativeLogin): Observable<LoginResponse> {
    const siqLogin = {
      login: action.payload.Email,
      password: action.payload.Password
    } as LoginModel;
    return this.authService.nativeLogin(siqLogin).pipe(
      tap({
        next: (result: LoginResponse) => {
          this.splashScreenService.setAuthLoading(false);
          ctx.patchState({ siqAuth: FunctionsHelper.encrypt(new LoginResponse(result)) });
          ctx.dispatch(new Navigate(['auth/password-update/update-your-password']));
        },
        error: error => {
          this.mixpanelService.trackLoginError(error.error);
          ctx.patchState({ loginError: FunctionsHelper.createSiqError(error.status, error.error.Error.Message) });
          return this.handleError(ctx, error, false);
        }
      })
    );
  }

  @Action(ReSiqLogin)
  reSiqLogin(ctx: StateContext<AuthStateModel>): Observable<LoginResponse> {
    const login = localStorage.getItem('login') ?? '';
    const loginDecrypted = FunctionsHelper.decrypt<CognitoLoginModel>(login);
    const siqLogin = {
      login: loginDecrypted.Email,
      password: loginDecrypted.Password
    };
    return this.authService.nativeLogin(siqLogin).pipe(
      tap({
        next: (result: LoginResponse) => ctx.patchState({ siqAuth: FunctionsHelper.encrypt(result) }),
        error: error => this.handleError(ctx, error, false)
      })
    );
  }

  @Action(UnpackToken)
  unpackToken(ctx: StateContext<AuthStateModel>): void {
    const cognitoAuth = ctx.getState().cognitoAuth;
    const auth = cognitoAuth ? FunctionsHelper.decrypt<CognitoQuery<CognitoLoginData | CognitoRefreshToken>>(cognitoAuth) : null;
    if (auth) {
      const username = FunctionsHelper.decodeJWT(auth.data.AccessToken)['username'];
      ctx.patchState({ username: FunctionsHelper.encrypt(username) });
      ctx.dispatch(new GetUserInformation());
    } else {
      this.splashScreenService.setAuthLoading(false);
    }
  }

  @Action(GetUserInformation)
  getUserInformation(ctx: StateContext<AuthStateModel>): Observable<UserInfo | SiqError> {
    const userInfo$ = this.authService.getUserInfo().pipe(
      map((data) => new UserInfo(data)),
      catchError((initialError: HttpErrorResponse) => of(FunctionsHelper.createSiqError(initialError.error.Error.Code, initialError.error.Error.Message)))
    );

    return userInfo$.pipe(
      tap({
        next: (response) => {
          const isUserInfoSuccessful = !(response instanceof SiqError);

          // Case 1: user/jwt is successful
          if(isUserInfoSuccessful) {
            const userInfo = response as UserInfo;
            ctx.patchState({ userInfo: FunctionsHelper.encrypt(userInfo) });
            // Case 1.1: user is already registered
            if(userInfo.registrationState === 13) {
              ctx.dispatch(new Navigate(['pages/select-default-sleeper']));
            } else { // Case 1.2: user has to go through registration
              // set TACO codes and redirect to terms-of-use
              if (!userInfo.licenseAccepted || !userInfo.privacyPolicyAccepted) {
                this.setTacoCodes(ctx, userInfo);
              }
              ctx.dispatch(new Navigate(['auth/terms-of-use']));
              this.splashScreenService.setAuthLoading(false);
            }
          } else { // Case 2: user/jwt is failed
            this.siqPopupHelper.showAlert('Auth');
            ctx.dispatch(new Logout());
            this.resetApp(ctx);
          }
        },
        error: () => this.resetApp(ctx)
      })
    );
  }

  @Action(GoToLogin)
  goToLogin(ctx: StateContext<AuthStateModel>): void {
    ctx.dispatch(new Navigate(['auth/login']));
    this.ngZone.runOutsideAngular(() => BootController.getBootControl().restart());
  }

  @Action(Logout)
  logout(ctx: StateContext<AuthStateModel>): Observable<object> {
    return this.authService.logout().pipe(
      tap({
        next: () => this.resetApp(ctx),
        error: error => {
          this.resetApp(ctx);
          return this.handleError(ctx, error, false);
        }
      })
    );
  }

  @Action(ResetAuthState)
  resetAuthState(ctx: StateContext<AuthStateModel>): void {
    ctx.setState({ ...defaultAuthState });
  }

  @Action(ResetStates)
  resetStates(ctx: StateContext<AuthStateModel>): void {
    ctx.dispatch(new ResetAuthState());
    ctx.dispatch(new ResetAppState());
    ctx.dispatch(new ResetBedState());
    ctx.dispatch(new ResetSleeperState());
    ctx.dispatch(new ResetSessionsState());
    ctx.dispatch(new ResetHealthState());
    ctx.dispatch(new ResetEdpState());
    ctx.dispatch(new ResetWhyIsThisState());
    ctx.dispatch(new ResetSettingsState());
    ctx.dispatch(new ResetRegisterState());
    ctx.dispatch(new ResetSetupState());
    ctx.dispatch(new ResetTacoState());
    ctx.dispatch(new ResetValidateLoginState());
    ctx.dispatch(new ResetForgotPasswordState());
    ctx.dispatch(new ResetPasswordRecoveryState());
  }

  @Action(SetRememberMe)
  setRememberMe(ctx: StateContext<AuthStateModel>, action: SetRememberMe): void {
    localStorage.setItem('isRememberMeClicked', RememberMe[action.payload]);
  }

  @Action(SetRegistrationState)
  setRegistrationState(ctx: StateContext<AuthStateModel>, action: SetRegistrationState): Observable<SleeperRegistrationState> {
    return this.authService.setRegistrationState(action.state).pipe(
      tap({
        next: (result: SleeperRegistrationState) => {
          const userInfo = FunctionsHelper.decrypt<UserInfo>(ctx.getState().userInfo);
          userInfo.registrationState = parseInt(result.registrationState, 10);
          ctx.patchState({
            userInfo: FunctionsHelper.encrypt(userInfo)
          });
        },
        error: error => this.handleError(ctx, error, true)
      })
    );
  }

  //#region Private functions
  private handleError(ctx: StateContext<AuthStateModel>, initialError: HttpErrorResponse, showAPIError: boolean): void {
    this.splashScreenService.setAuthLoading(false);
    if (initialError.status >= SiqErrorCodes.BadRequest && initialError.status < SiqErrorCodes.Server && initialError.status !== SiqErrorCodes.Unauthorized) {
      const errorMessage = showAPIError ? initialError.error.Error.Message : ServerErrors.ApiErrors.error400.text;
      this.siqPopupHelper.showAlert('Auth', errorMessage);
    }
    ctx.patchState({ error: FunctionsHelper.createSiqError(initialError.error.Error.Code, initialError.error.Error.Message), loading: false });
  }

  private handleCognitoLoginError(ctx: StateContext<AuthStateModel>, cognitoError: HttpErrorResponse): Observable<HttpErrorResponse> {
    this.splashScreenService.setAuthLoading(false);
    if (cognitoError.status === SiqErrorCodes.BadRequest) {
      if (cognitoError.error.data.code === CognitoErrorCodes.UserNotFoundException) {
        const login = localStorage.getItem('login') ?? '';
        ctx.dispatch(new NativeLogin(FunctionsHelper.decrypt<CognitoLoginModel>(login)));
      } else if (cognitoError.error.data.code === CognitoErrorCodes.NotAuthorizedException) {
        ctx.patchState({ loginError: FunctionsHelper.createSiqError(SiqErrorCodes.Unauthorized, cognitoError.error.data.message) });
      }
    } else {
      ctx.patchState({ loginError: FunctionsHelper.createSiqError(cognitoError.status, cognitoError.error.data.message) });
    }
    this.mixpanelService.trackLoginError(cognitoError.error);
    // @ts-expect-error this should work as expected
    this.mixpanelService.trackError(cognitoError.url, cognitoError.error.data.code, cognitoError.error.data.message, cognitoError.status);
    throw cognitoError;
  }

  private resetApp(ctx: StateContext<AuthStateModel>): void {
    this.splashScreenService.setAuthLoading(false);
    const loggedInSleeperId = ctx.getState().userInfo ? (FunctionsHelper.decrypt<UserInfo>(ctx.getState().userInfo)).sleeperId : '';
    ctx.dispatch(new ResetStates());
    ctx.dispatch(new UnsubscribeFromBedStatus());
    try {
      const isRememberMeClicked = localStorage.getItem('isRememberMeClicked') as keyof typeof RememberMe;
      if (isRememberMeClicked && !RememberMe[isRememberMeClicked]) {
        localStorage.removeItem('login');
      }
      localStorage.removeItem('isRememberMeClicked');
      localStorage.removeItem('setup');
      localStorage.removeItem('register');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('app');
      localStorage.removeItem('auth');
      localStorage.removeItem('taco');
      localStorage.removeItem(`defaultSleeper${loggedInSleeperId}`);
      this.mixpanelService.trackLogout();
      this.mixpanelService.reset();
    } catch (exception) {
      this.mixpanelService.trackLogout();
      this.mixpanelService.reset();
    }
    ctx.dispatch(new GoToLogin());
  }

  private setTacoCodes(ctx: StateContext<AuthStateModel>, userInfo: UserInfo): void {
    if (!userInfo.licenseAccepted) {
      ctx.dispatch(new SetTacoState(ErrorCodeHelper.tacoCodes.license, false));
    }
    if (!userInfo.privacyPolicyAccepted) {
      ctx.dispatch(new SetTacoState(ErrorCodeHelper.tacoCodes.privacyPolicy, false));
    }
  }

  private calculateTokenExpirationTime(expiresIn: number): number {
    const now = Date.now(); // ms
    return now + expiresIn * 1000;
  }
  //#endregion


  
}